import { Link, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PaymentMethodData } from 'shared/types';
import { Capacitor } from '@capacitor/core';

type SubscriptionActionsProps = {
  isIosSubscription: boolean;
  isRecurringSubscription: boolean;
  paymentType?: PaymentMethodData['paymentType'];
  hostedLink?: string;
};

type ExternalLinkProps = {
  href: string;
  label: string;
};

const getEditBillingInfoLink = (accountLink: string): string => {
  const url = new URL(accountLink);
  const accountHash = url.pathname.split('/').pop();

  if (!accountHash || accountHash === 'account') return accountLink;

  url.pathname = '/account/billing_info/edit';
  url.searchParams.set('ht', accountHash);

  return url.toString();
};

export const ExternalLink = (props: ExternalLinkProps): JSX.Element => {
  const { href, label } = props;

  return (
    <Button
      fullWidth
      size="large"
      color="neutral"
      component={Link}
      href={href}
      target="_blank"
      disabled={!href}
    >
      {label}
    </Button>
  );
};

export const SubscriptionActions = (
  props: SubscriptionActionsProps,
): JSX.Element | null => {
  const {
    isIosSubscription,
    isRecurringSubscription,
    paymentType,
    hostedLink,
  } = props;
  const { t } = useTranslation();

  if (isIosSubscription) {
    if (Capacitor.getPlatform() === 'ios') {
      return (
        <Button
          fullWidth
          size="large"
          onClick={() =>
            CdvPurchase.store.manageSubscriptions(
              CdvPurchase.Platform.APPLE_APPSTORE,
            )
          }
          color="neutral"
        >
          {t('membership.subscription.manageSubscription')}
        </Button>
      );
    }

    return (
      <ExternalLink
        label={t('membership.subscription.manageSubscription')}
        href="https://account.apple.com/account/manage/section/subscriptions"
      />
    );
  }

  if (!paymentType || !hostedLink) {
    return null;
  }

  if (paymentType === 'credit_card' && isRecurringSubscription) {
    return (
      <Stack spacing={1}>
        <ExternalLink
          href={getEditBillingInfoLink(hostedLink)}
          label={t('membership.subscription.editBillingInfo')}
        />
        <ExternalLink
          href={hostedLink}
          label={t('membership.subscription.manageSubscription')}
        />
      </Stack>
    );
  }

  return (
    <ExternalLink
      href={hostedLink}
      label={t('membership.subscription.manageSubscription')}
    />
  );
};
